import React, { useState, useRef, useCallback } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import './VoiceInput.css';

interface VoiceInputProps {
  onTranscriptChange: (transcript: string) => void;
  onTranscriptComplete?: (transcript: string) => void;
  defaultMode?: 'slow' | 'fast';
  className?: string;
}

const VoiceInput: React.FC<VoiceInputProps> = ({
  onTranscriptChange,
  onTranscriptComplete,
  defaultMode = 'fast',
  className = '',
}) => {
  const { language } = useLanguage();
  const [isListening, setIsListening] = useState(false);
  const [interimTranscript, setInterimTranscript] = useState('');
  const [transcript, setTranscript] = useState('');
  const recognitionRef = useRef<any>(null);

  // Функція для очищення тексту
  const handleClear = useCallback(() => {
    setTranscript('');
    setInterimTranscript('');
    onTranscriptChange('');
  }, [onTranscriptChange]);

  const startVoiceSearch = (mode: 'slow' | 'fast') => {
    console.log(`🎤 Запуск голосового пошуку. Режим: ${mode}`);

    // Перевірка підтримки Web Speech API
    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;

    if (!SpeechRecognition) {
      console.error('❌ Web Speech API не підтримується');
      const message =
        language === 'ua'
          ? 'Голосове розпізнавання не підтримується вашим браузером'
          : 'Voice recognition is not supported by your browser';
      alert(message);
      return;
    }

    const recognition = new SpeechRecognition();
    recognitionRef.current = recognition;

    // Налаштування мови з варіантами для Web Speech API
    let langCode = 'en-US';
    if (language === 'ua') {
      langCode = 'uk-UA';
    } else if (language === 'en') {
      langCode = 'en-US';
    }

    recognition.continuous = false;
    recognition.interimResults = true;
    recognition.lang = langCode;

    console.log(`🌐 Встановлена мова: ${langCode} (поточна мова в app: ${language})`);

    // Налаштування темпу
    if (mode === 'slow') {
      recognition.maxAlternatives = 1;
      console.log('🐢 Повільний режим: maxAlternatives = 1');
    } else {
      recognition.maxAlternatives = 3;
      console.log('🚀 Швидкий режим: maxAlternatives = 3');
    }

    setIsListening(true);
    setInterimTranscript('');

    let finalTranscript = '';
    let lastInterimTranscript = '';

    recognition.onstart = () => {
      console.log(`✅ Розпізнавання розпочато. Мова: ${langCode}`);
      setIsListening(true);
    };

    recognition.onresult = (event: any) => {
      let interim = '';
      console.log(`📝 Результат #${event.resultIndex}:`);

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        const confidence = event.results[i][0].confidence;

        console.log(
          `   [${i}] Текст: "${transcript}" | Впевненість: ${(confidence * 100).toFixed(2)}% | Фінальний: ${event.results[i].isFinal}`
        );

        if (event.results[i].isFinal) {
          console.log(`✔️ Фінальний результат: "${transcript}"`);
          finalTranscript += transcript + ' ';
          setTranscript(finalTranscript.trim());
          onTranscriptChange(finalTranscript.trim());
        } else {
          interim += transcript;
        }
      }

      // Показ проміжних результатів
      if (interim && interim !== lastInterimTranscript) {
        lastInterimTranscript = interim;
        setInterimTranscript(interim);
        console.log(`⏳ Проміжний результат: "${interim}"`);
      }
    };

    recognition.onerror = (event: any) => {
      console.error('❌ Помилка голосового розпізнавання:', event.error);

      // Деталізація помилок
      switch (event.error) {
        case 'no-speech':
          console.error('   → Не виявлено мови. Спробуйте говорити гучніше.');
          break;
        case 'audio-capture':
          console.error('   → Проблема з мікрофоном. Перевірте дозволи доступу.');
          break;
        case 'network':
          console.error('   → Проблема з мережею.');
          break;
        case 'bad-grammar':
          console.error('   → Помилка граматики.');
          break;
        case 'service-not-available':
          console.error('   → Сервіс розпізнавання недоступний.');
          break;
        default:
          console.error(`   → Невідома помилка: ${event.error}`);
      }

      console.log(`🌐 Поточна мова розпізнавання: ${langCode}`);
      setIsListening(false);
    };

    recognition.onend = () => {
      console.log('🛑 Розпізнавання завершено');
      console.log(`📊 Фінальний накопичений текст: "${finalTranscript}"`);
      setIsListening(false);
      setInterimTranscript('');

      if (onTranscriptComplete && finalTranscript.trim()) {
        onTranscriptComplete(finalTranscript.trim());
      }
    };

    console.log('🎙️ Починаю слухати...');
    recognition.start();
  };

  const stopVoiceSearch = () => {
    console.log('⏹️ Зупинка голосового розпізнавання');
    if (recognitionRef.current) {
      recognitionRef.current.stop();
      setIsListening(false);
      setInterimTranscript('');
    }
  };

  return (
    <div className={`voice-input-wrapper ${className}`}>
      {/* Іконка очистки */}
      {transcript && !isListening && (
        <button
          className="clear-input-btn"
          onClick={handleClear}
          title={language === 'ua' ? 'Очистити' : 'Clear'}
          aria-label={language === 'ua' ? 'Очистити' : 'Clear'}
          style={{ color: '#ff4444' }}
        >
          <i className="bi bi-x-circle" style={{ color: '#ff4444' }}></i>
        </button>
      )}

      {/* Мікрофон - показується тільки коли немає тексту або коли йде запис */}
      {(!transcript || isListening) && (
        <button
          className={`voice-input-btn ${isListening ? 'active' : ''}`}
          onClick={() => (isListening ? stopVoiceSearch() : startVoiceSearch(defaultMode))}
          title={
            isListening
              ? language === 'ua'
                ? 'Зупинити'
                : 'Stop'
              : language === 'ua'
                ? 'Голосовий пошук'
                : 'Voice search'
          }
        >
          <i className={`bi ${isListening ? 'bi-stop-circle-fill' : 'bi-mic'}`}></i>
        </button>
      )}

      {interimTranscript && isListening && (
        <span className="interim-transcript">{interimTranscript}</span>
      )}
    </div>
  );
};

export default VoiceInput;
